import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { fetchUpcomingAppointment } from "../../../../shared/hooks/useFetchAppointment";
import API_URLS from "../../Utils/axr";
import {
  checkIsPastDate,
  checkIsUpcommingAppointment,
} from "../../../../shared/utils/dateUtils";
import { errorConditionGTM } from "../../Utils/googleTags";
import store, { RootState } from "../store";
import DwLogutil from "../../Utils/DwLogutil";
import { EC_DW_LOGGING_STEPS } from "../../../../shared/utils/Constants";
import {
  getServiceAddressFromUser,
  shouldSkipFailedOutApiFetch,
  shouldStopApiRetry,
  logApiRetryCapDwLog,
} from "../../Utils/utils";
import { createNotification72hContext } from "../../Utils/notification72HourContext";
import {waitForApiCall} from "../../Utils/waitForApiCall";
import { hasCompletedOrCanceledBuriedDrop } from "../../Utils/buriedDropStatus";

type upcomingAppointmentInitialStateType = {
  upcomingAppointment: any;
  rescheduleStatus: string;
  loading: boolean;
  error: any;
  fetched: boolean;
  retryCount: number;
  rescheduleOrderNumber: any;
  isTempable1or2: any;
};
const initialState: upcomingAppointmentInitialStateType = {
  upcomingAppointment: null,
  rescheduleStatus: localStorage.getItem("rescheduling") || "no",
  loading: false,
  error: null,
  fetched: false,
  retryCount: 0,
  rescheduleOrderNumber: null,
  isTempable1or2: null,
};

// export const getUpcomingAppointment = createAsyncThunk(
//   "upcoming/appointment",
//   async (user: any) => {
//     try {
//       const { billingAccountNumber, email } = user;
//       const response = await fetchUpcomingAppointment({
//         // [API_URLS?.upComingAppointmentUrl]: `${billingAccountNumber}?email=${email}`,
//         [API_URLS?.fetchPastAppointments]: `?email=${email}`
//       });
//       return response?.data;
//     } catch (error) {
//       throw error;
//     }
//   }
// );

export const getUpcomingAppointment = createAsyncThunk(
  "upcoming/appointment",
  async (user: any) => {
    try {
      const { billingAccountNumber, email, isFromOtherAgent } = user;
      const serviceAddLocal = getServiceAddressFromUser(user);
      const reduxStore = store.getState();
      const headers = reduxStore?.brightspeedToken?.headers;
      const notificationState = await waitForApiCall(
        store,
        (state: RootState) => state.notificationDetails,
        (nd: any) =>
          nd?.fetched === true &&
          nd?.notificationLoading === false,
        4000
      );
      const notificationsResolved = notificationState?.fetched === true; 
      const notifications = notificationState?.notificationResult?.notifications || [];
      const { closedOrders } =
        createNotification72hContext(notifications, {
          deliveredKey: "techInstallCompleted",
          closedKey: "techInstallClosed",
        });
      let url=API_URLS['upComingAppointmentUrl_QA'];
      // TEST: mimic auth failure on user-appointments only (other APIs keep store headers).
      const upcomingAppointmentRequestHeaders = headers;
      // const upcomingAppointmentRequestHeaders = {
      //   ...headers,
      //   Authorization: null,
      //   "access-token": null,
      // };
      const response = await fetchUpcomingAppointment(
        {
          "": `${
            process.env.REACT_APP_ENV === "QA"
              ? url
              : API_URLS?.fetchPastAppointments
          }?ban=${billingAccountNumber}&addressId=${
            serviceAddLocal?.addressId
          }`,
        },
        upcomingAppointmentRequestHeaders
      );
      const respData = response?.data || [{ userAppointments: [] }];
      // const respData: any = [];

      const sortedAppointmentDataForOrderNumber =
        respData?.userAppointments?.[0]?.order?.sort(
          (a: any, b: any) =>
            new Date(b?.apptDetails?.appointmentDate)?.valueOf() -
            new Date(a?.apptDetails?.appointmentDate)?.valueOf()
        );
      const validOrderStatus = sortedAppointmentDataForOrderNumber?.length > 0
        ? sortedAppointmentDataForOrderNumber?.filter(
          (item: any) => !(item?.orderStatus?.toLowerCase()?.includes("object"))
        ) || ""
        : "";
      const latestAppontmentDetails =
      validOrderStatus?.length > 0
          ? validOrderStatus?.find(
              (item: any) => item?.orderNumber && item?.orderStatus?.toLowerCase() !== "canceled"
            ) || ""
          : "";
      const pendingOrderDetails = respData?.userAppointments[0]?.order?.filter((anOrder: any) => {
          const status = anOrder?.orderStatus?.toLowerCase()?.trim() ?? "";
          const orderNumber = anOrder?.orderNumber;

          if (closedOrders.has(orderNumber)) {
            return false;
          }

          if (status === "completed") {
            return checkIsUpcommingAppointment(
              anOrder?.apptDetails?.appointmentDate
            );
          }
          

          if (
            !isFromOtherAgent &&
            anOrder?.installType?.toUpperCase() !== "S" &&
            anOrder?.apptDetails?.appointmentDate &&
            anOrder?.apptDetails?.appointmentStartTime &&
             status !== "canceled" && status !== "completed" && !status?.includes("cancel")
          ) {
            if (
              !anOrder?.dispatchInfo?.hasOwnProperty("taskStatus") ||
              !anOrder?.dispatchInfo
            ) {
              DwLogutil({
                stepName:
                  EC_DW_LOGGING_STEPS.invalid_upcomming_appointment_dispatch_info,
                error: "Invalid dispatch info",
              });
              return false;
            }
            return true;
          } else if (
            isFromOtherAgent &&
            anOrder?.apptDetails?.appointmentDate &&
            anOrder?.apptDetails?.appointmentStartTime &&
            status !== "canceled" &&
            !status.includes("cancel") &&
            status !== "cannot complete"
          ) {
            return true;
          }

          return false;
        }) ?? [];
      const respDataMapped = {
        upcomingAppointments: [
          {
            orderDetails: {
              pendingDisconnectDate: respData?.userAppointments[0]?.disconnectDueDate || null,
              pendingOrderDetails: pendingOrderDetails,
              appointmentOrderNumbersForCloseFunction: [
                ...new Set(pendingOrderDetails.map((order: any) => order.orderNumber)
                ),
              ],
              pastAppointment: respData?.userAppointments[0]?.order?.reduce(
                (acc: any[], anOrder: any) => {
                  const appointmentDate =
                    anOrder?.apptDetails?.appointmentDate?.trim() ||
                    anOrder?.dueDate?.trim();
                  if (acc.length === 0) {
                    acc.push({ burriedDropInfo: [] });
                  }
                  if (anOrder?.installType?.toUpperCase() === "S") {
                    return acc;
                  }
                  const isApptCompletedOrCanceled =
                    checkIsPastDate(appointmentDate) &&
                    (anOrder?.orderStatus?.toLowerCase() === "canceled" ||
                    anOrder?.orderStatus?.toLowerCase() === "completed" ||
                    anOrder?.orderStatus?.toLowerCase() === "cannot complete" ||
                    anOrder?.orderStatus?.toLowerCase()?.includes("cancel"));

                  // burriedDropInfo-side: buried drop job is complete/completed/canceled/cancelled.
                  const isBuriedDropCompletedOrCanceled =
                    hasCompletedOrCanceledBuriedDrop(anOrder?.burriedDropInfo);
                  if (isApptCompletedOrCanceled) {
                    acc.push(anOrder);
                  }  
                  if (isBuriedDropCompletedOrCanceled) {
                    acc[0].burriedDropInfo.push(anOrder.burriedDropInfo);
                  }
                  return acc;
                },
                []
              ),
            },
          },
        ],
        orderId: latestAppontmentDetails?.orderNumber || "",
        orderStatusForWallgarden: latestAppontmentDetails?.orderStatus || "",
      };
      return respDataMapped;
    } catch (error) {
      console.error(error);
      DwLogutil({
        stepName: EC_DW_LOGGING_STEPS?.upcoming_appointment_failure,
        error: `${error}`
      })
      throw error;
    }
  },
  {
    condition: (_, { getState }) => {
      const state = (getState() as RootState).upComingAppointment;
      if (state.loading) {
        return false;
      }
      if (shouldSkipFailedOutApiFetch(!!state.upcomingAppointment, state.fetched)) {
        return false;
      }
      return true;
    },
  }
);

const upcomingAppointmentSlice = createSlice({
  name: "upcoming/appointment",
  initialState,
  reducers: {
    clearUpcomingAppointmentSlice: (state) => {
      return initialState;
    },
    setRescheduleStatus: (state, action) => {
      state.rescheduleStatus = action.payload;
      return state;
    },
    setRescheduleOrderNumber: (state, action) => {
    state.rescheduleOrderNumber = action.payload;
    },
    setIsTempable1or2: (state, action) => {
      state.isTempable1or2 = action.payload;
    },
    upcomingAppointmentSliceSetInitialState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getUpcomingAppointment.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getUpcomingAppointment.fulfilled, (state, action) => {
      state.upcomingAppointment = action.payload;
      state.loading = false;
      state.error = null;
      state.retryCount = 0;
      state.fetched = true;
    });
    builder.addCase(getUpcomingAppointment.rejected, (state, action) => {
      errorConditionGTM(
        "BRSPD_Fiber_EC_Flow",
        "upcomming appointment",
        action.error?.message,
        action?.type
      );
      state.upcomingAppointment = null;
      state.loading = false;
      state.error = action.error;
      state.retryCount += 1;
      if (shouldStopApiRetry(state.retryCount)) {
        state.fetched = true;
        logApiRetryCapDwLog(
          EC_DW_LOGGING_STEPS.upcoming_appointment_API_retry_cap,
          action.error?.message
        );
      }
    });
  },
});

export const {
  clearUpcomingAppointmentSlice,
  setRescheduleStatus,
  setRescheduleOrderNumber,
  setIsTempable1or2,
  upcomingAppointmentSliceSetInitialState,
} = upcomingAppointmentSlice.actions;
export default upcomingAppointmentSlice.reducer;
